import { Form, Row, Col, InputGroup, Button } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { error as errorAlert, infoAlert, successAlert, warningAlert } from '../../components/toastr';
import { SubmitButton, CancelButton } from '../../components/btns';
import { DateSelector } from '../../components/datetimepicker';
import moment from 'moment';
import { getEvents } from '../../resources/api/events';
import SelectInput, { Select } from '../../components/select';
import cur, { UGX } from '../../components/currency';
import { createPayment, updatePayment } from '../../resources/api/payments';
import { useNavigate } from 'react-router-dom';
import { assortedPaymentMethods, paymentMethods } from '../../assets/constants';
import { UCWords } from '../../components/resources';
import { getAccounts } from '../../resources/api/accounts';
import { useMemo } from 'react';
import { getClients } from '../../resources/api/clients';

const { Control, Control: { Feedback }, Label } = Form;

/**
 * handle page for creating payment
 * @param {Object} props
 * @param {"edit"|"create"} props.action
 * @param {import('../../resources/api/payments').PaymentObject} props.details
 * @param {(details: import('../../resources/api/payments').PaymentObject) => void} props.onSubmit
 */
const PaymentForm = ({ details: paymentObject, action, onSubmit }) => {


    const [details, setDetails] = useState(paymentObject);
    const [prods, setProds] = useState(paymentObject.items);
    const [deleted_prods, setDeletedProds] = useState([]);
    const [selected_events, setSelectedEvents] = useState([]);
    const [display_events, setDisplayEvents] = useState([]);

    const [clients, setClients] = useState([]);
    const [events, setEvents] = useState([]);
    const [accounts, setAccounts] = useState([]);

    const [validated, setValidated] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    const changeProdAmount = (amount, idx) => {
        const _prods = [...prods];

        let _prod = _prods[idx];
        _prod = { ..._prod, amount, hasChanged: 1 };
        _prods.splice(idx, 1, _prod);
        setProds(_prods);
    }

    const displayAccounts = useMemo(() => {

        const _types = assortedPaymentMethods[details.method] || [];

        return accounts.filter(a => _types.indexOf(a.account_type) !== -1);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details.method, JSON.stringify(accounts)])


    const deleteProd = idx => {
        const _prod = [...prods][idx];
        setProds(prods => prods.filter((e, i) => i !== idx));
        setSelectedEvents(ids => ids.filter(i => (i !== _prod.event_id)));
        if (!!_prod.id) setDeletedProds(p => p.concat(_prod.id));

    }

    const handleEventSelect = ids => {
        let items = events
            .filter(i => ids.indexOf(i.id) !== -1)
            .map(i => ({
                event_id: i.id,
                event: i.reference,
                balance: i.balance,
                amount: 0,
                isNew: true
            }))

        setSelectedEvents(ids => [...ids, ...items.map(i => i.event_id)]);
        setProds(prev => ([...prev, ...items]));
    }

    useEffect(() => {

        setDisplayEvents(
            events.filter(i => (selected_events.indexOf(i.id) === -1))
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selected_events), JSON.stringify(events)])

    useEffect(() => {
        infoAlert("Loading accounts...");

        getAccounts()
            .then(({ accounts }) => {
                if (accounts.length === 0)
                    warningAlert("No accounts available. Please add an account first.");
                else
                    successAlert("Loaded all accounts.");

                setAccounts(accounts);
            })
            .catch(errorAlert)
    }, [])

    /**
     * handle the overall submitting of the form
     * @param {React.FormEvent} e
     */
    const handleSubmit = e => {
        const form = e.currentTarget;

        e.preventDefault();

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("You have errors in your form. These have been highlighted for you.", "Form Errors");
            return;
        }

        setValidated(false);
        setSubmitting(true);

        let promise;

        if (action === "create") {
            promise = createPayment({
                ...details,
                items: prods.filter(p => !!parseInt(p.amount))
            });
        } else {
            promise = updatePayment({
                ...details,
                new_items: prods.filter(p => (!p.id && !!parseInt(p.amount))),
                updated_items: prods.filter(p => (!!p.id && p.hasChanged)),
                deleted_items: deleted_prods
            }, details.id);
        }

        promise
            .then(({ payment, message }) => {
                successAlert(message);
                onSubmit(payment);
            })
            .catch(e => {
                errorAlert(e);
                setSubmitting(false);
            })
    }

    useEffect(() => {
        getClients()
            .then(({ clients }) => {
                setClients(
                    clients
                        // .filter(e => (e.balance > 0))
                        .map(e => ({
                            ...e,
                            title: e.title
                        })));
            })
            .catch(e => errorAlert(e));
    }, [])

    useEffect(() => {

        setEvents([]);
        setProds([]);


        if (!details.client_id) return;

        infoAlert("Loading events for this client...")

        getEvents(false, { client_id: details.client_id, payment_status: ['unpaid', 'partially-paid'] })
            .then(({ events }) => {
                setEvents(events.map(i => ({
                    ...i,
                    title: i.reference,
                    description: `${moment(i.event_date).format("DD MMM YYYY")} | ${UGX(i.amount).format()}`
                })));

                if (events.length > 0)
                    successAlert("Successfully loaded all client's events")
                else
                    warningAlert("Client has no unpaid events");
            })
            .catch(e => errorAlert(`Could not get unpaid events for the selected client. ${e}`));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details.client_id])

    useEffect(() => {

        if (action === "edit") {
            setProds(paymentObject.items.map(p => ({ ...p, balance: p.amount + p.balance })))
            setSelectedEvents(paymentObject.items.map(i => i.event_id));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action])


    return (
        <Form className="max-800" validated={validated} noValidate onSubmit={handleSubmit}>
            <h4 className="form-section-label">Details</h4>
            <Row className="g-2">
                {(action === "create") &&
                    <Col sm={6} className="my-1">
                        <Label className="form-field-title">Client</Label>
                        <SelectInput
                            value={[details.client_id]}
                            placeholder='-'
                            onChange={([id]) => setDetails(d => ({
                                ...d,
                                client_id: id || ""
                            }))}
                            required
                            maxItems={1}
                            options={clients}
                            errorText="A client must be chosen inorder to get the list of unpaid jobs to pay against."
                        />
                    </Col>
                }
                {(action === "edit") &&
                    <Col sm={6} className="my-1">

                        <Control
                            value={details.client.title}
                            readOnly
                        />
                    </Col>
                }
            </Row>
            <Row className="g-2">
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Payment Date</Label>
                    <DateSelector
                        value={details.payment_date}
                        onChange={val => setDetails(d => ({ ...d, payment_date: val }))}
                        placeholder="-"
                        required={true}
                        // minDate={moment().subtract(1, "month").format("YYYY-MM-DD")}
                        minDate="2021-01-01"
                    />
                    <Feedback type="invalid">
                        The payment date should be defined.
                    </Feedback>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Paid By</Label>
                    <Control
                        value={details.payee}
                        onChange={e => setDetails({ ...details, payee: e.currentTarget.value })}
                        placeholder="-"
                        required={true}
                    />
                    <Feedback type="invalid">
                        A valid name must be provided
                    </Feedback>
                </Col>
            </Row>
            <Row>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Payment Method</Label>
                    <Form.Select
                        as="select"
                        value={details.method}
                        onChange={e => setDetails({ ...details, method: e.currentTarget.value, account_id: "" })}
                        required
                    >
                        <option value="">-</option>
                        {paymentMethods.map(c => <option key={c} value={c}>{UCWords(c).replace(/-/g, " ")}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select a method for payment for this expense.
                    </Feedback>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Payment Account</Label>
                    <Form.Select
                        as="select"
                        value={details.account_id}
                        onChange={e => setDetails({ ...details, account_id: e.currentTarget.value })}
                        required
                    >
                        <option value="">-</option>
                        {displayAccounts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select the account to which payment is being made.
                    </Feedback>
                    {!details.method &&
                        <Form.Text muted>
                            First select a payment method.
                        </Form.Text>
                    }
                </Col>
            </Row>

            <h4 className="form-section-label">Events</h4>

            <Row className="d-none d-sm-flex">
                <Col sm={5}><Label className="form-field-title">Event</Label></Col>
                <Col sm={3}><Label className="form-field-title">Balance</Label></Col>
                <Col sm={4}><Label className="form-field-title">Amount Paid</Label></Col>
            </Row>

            {prods.map((p, idx) => (
                <Row key={idx} className="my-3 my-sm-1 border-bottom pb-3 g-2">
                    <Col md={5} sm={12}>
                        <Label className="form-field-title  d-sm-none">Event</Label>
                        <Control
                            readOnly
                            value={p.event}
                        />

                    </Col>
                    <Col md={3} sm={6}>
                        <Label className="form-field-title d-sm-none">Balance</Label>
                        <Control
                            readOnly
                            value={cur(p.balance, 0).format()}
                        />

                    </Col>
                    <Col md={4} sm={6}>
                        <Label className="form-field-title d-sm-none">Amount Paid</Label>
                        <InputGroup>
                            <Control
                                type="number"
                                value={p.amount}
                                min={500}
                                max={Math.ceil(p.balance)}
                                onChange={e => changeProdAmount(e.currentTarget.value, idx)}
                                placeholder="Amount"
                                step={0.05}
                            />
                            <Button variant="link" size="sm" className='text-danger' onClick={() => deleteProd(idx)}>
                                <i className="fas fa-times" />
                            </Button>
                        </InputGroup>
                        <Control.Feedback type="invalid">
                            Amount must be less than {cur(p.balance, 0).format()} but more than 500
                        </Control.Feedback>
                    </Col>
                </Row>
            ))}



            <Row>
                <Col className='my-2'>
                    <Select
                        items={display_events}
                        size="sm"
                        className='rounded-pill'
                        maxItems={10}
                        onSelect={handleEventSelect}
                    >
                        <i className='fas fa-circle-plus me-1' />Add Event(s)
                    </Select>
                </Col>
                <Col className='my-2 text-end h4'>
                    {UGX(prods.reduce((p, c) => p + parseFloat(c.amount), 0)).format()}
                </Col>
            </Row>


            <Row>
                <Col className="mt-4 mb-3 text-end">
                    <SubmitButton isSubmitting={isSubmitting} type="submit">
                        {action === "edit" ? "Update Payment" : "Create Payment"}
                    </SubmitButton>
                    <CancelButton isSubmitting={isSubmitting} onClick={() => navigate(-1)}>
                        Cancel
                    </CancelButton>
                </Col>
            </Row>
        </Form>
    )
}


export default PaymentForm;